import React, { memo, useCallback } from "react";
import { View, Text } from "../platformComponents";
import { AsFormItemLayout } from "./AsFormItemLayout";
import { KycLabelElement } from "./KycLabelElement";
import { AsInput } from "./AsInput";
import { KycDateTimePicker } from "./KycDateTimePicker";
import { isEqual } from "lodash-es";
import { CommonCheckbox } from "../common";
import { margin, padding, colors, fontSize, fontFamily } from "../styles/common";
import { dateFormat, i18nCrossUtil, IS_WEB } from "@adgm/libs";
import type {
  IMultiCheckboxSubFormProps,
  IOptionConfig,
  ISubFormFieldConfig,
} from "./MultiCheckboxSubForm.types";
import { GenericFormProvider, useGenericFormContext } from "./GenericForm/GenericFormContext";

/**
 * 通用多选复选框+子表单组件
 *
 * @description
 * 支持多个复选框选项，每个选项选中后显示对应的子表单。
 * 通过配置对象驱动，完全通用化，不依赖特定数据结构。
 *
 * @example
 * ```tsx
 * const config: IMultiCheckboxSubFormConfig = {
 *   options: [
 *     {
 *       label: "Salary / Bonus from employment",
 *       fieldKey: "salary",
 *       subFormFields: [
 *         { name: "employerName", label: "Name of employer", type: "input", required: true },
 *       ],
 *       createInitialValue: () => ({ employerName: "" }),
 *     },
 *   ],
 * };
 *
 * <MultiCheckboxSubForm
 *   label="What are your current sources of income"
 *   value={formData}
 *   onChange={setFormData}
 *   config={config}
 * />
 * ```
 */
export function MultiCheckboxSubForm<T extends Record<string, any> = Record<string, any>>({
  value = null,
  onChange,
  error,
  label,
  tip,
  hideErrorSpace,
  sequenceNumber,
  style,
  required,
  subLabel,
  config,
}: IMultiCheckboxSubFormProps<T>) {
  const optionSpacing = config.optionSpacing ?? margin.m16;
  const subFormLeftPadding = config.subFormLeftPadding ?? padding.p24;
  const { genericFormType } = useGenericFormContext();
  const isQuestionnaireForm = genericFormType === "questionnaire";

  /**
   * 处理 checkbox 选项变化
   */
  const handleCheckboxChange = useCallback(
    (option: IOptionConfig, checked: boolean) => {
      const currentValue = (value ?? {}) as T;
      const newValue = { ...currentValue } as Record<string, any>;

      if (checked) {
        // 选中：创建对应的初始值
        newValue[option.fieldKey] = option.createInitialValue();
      } else {
        // 取消选中：删除属性
        const { [option.fieldKey]: _, ...rest } = newValue;
        return onChange(rest as T);
      }

      onChange(newValue as T);
    },
    [value, onChange],
  );

  /**
   * 处理子表单字段更新
   */
  const handleSubFieldChange = useCallback(
    (fieldKey: string, fieldValue: Record<string, any>) => {
      const currentValue = (value ?? {}) as T;
      onChange({
        ...currentValue,
        [fieldKey]: fieldValue,
      } as T);
    },
    [value, onChange],
  );

  /**
   * 获取字段标签文本
   */
  const getFieldLabel = useCallback((fieldConfig: ISubFormFieldConfig): string => {
    if (typeof fieldConfig.label === "function") {
      return fieldConfig.label();
    }
    // 如果是字符串，尝试作为i18n key
    try {
      const crossIntl = i18nCrossUtil.t();
      return crossIntl[fieldConfig.label] || fieldConfig.label;
    } catch {
      return fieldConfig.label;
    }
  }, []);

  /**
   * 获取选项描述文本
   */
  const getOptionDescription = useCallback((option: IOptionConfig): string | null => {
    if (!option.description) return null;
    if (typeof option.description === "function") {
      return option.description();
    }
    // 如果是字符串，尝试作为i18n key
    try {
      const crossIntl = i18nCrossUtil.t();
      return crossIntl[option.description] || option.description;
    } catch {
      return option.description;
    }
  }, []);

  /**
   * 渲染子表单字段
   */
  const renderSubFormField = useCallback(
    (fieldConfig: ISubFormFieldConfig, fieldValue: any, onFieldChange: (val: string) => void) => {
      const labelText = getFieldLabel(fieldConfig);

      switch (fieldConfig.type) {
        case "input":
          return (
            <View style={{ marginTop: 12 }}>
              <KycLabelElement label={labelText} required={fieldConfig.required} horizontalLayout />
              <AsInput
                key={fieldConfig.name}
                value={fieldValue || ""}
                onValueChange={onFieldChange}
                style={{ marginTop: -20 }}
              />
            </View>
          );
        case "datePicker":
          return (
            <View style={{ marginTop: 12 }}>
              <KycLabelElement label={labelText} required={fieldConfig.required} horizontalLayout />
              <KycDateTimePicker
                key={fieldConfig.name}
                value={fieldValue || ""}
                onValueChange={onFieldChange}
                dateFormat={fieldConfig.dateFormat || dateFormat}
                style={{ marginTop: IS_WEB ? -12 : -6 }}
              />
            </View>
          );
        default:
          return null;
      }
    },
    [getFieldLabel],
  );

  /**
   * 渲染子表单
   */
  const renderSubForm = useCallback(
    (option: IOptionConfig) => {
      const subFormData = value?.[option.fieldKey] as Record<string, any> | undefined;
      if (!subFormData) return null;

      return (
        <View style={{ marginTop: optionSpacing, marginLeft: subFormLeftPadding }}>
          {option.subFormFields.map(fieldConfig => {
            const fieldValue = subFormData[fieldConfig.name];
            const onFieldChange = (val: string) => {
              handleSubFieldChange(option.fieldKey, {
                ...subFormData,
                [fieldConfig.name]: val,
              });
            };
            return renderSubFormField(fieldConfig, fieldValue, onFieldChange);
          })}
        </View>
      );
    },
    [value, optionSpacing, subFormLeftPadding, handleSubFieldChange, renderSubFormField],
  );

  /**
   * 渲染单个选项及其子表单
   */
  const renderOption = useCallback(
    (option: IOptionConfig, index: number) => {
      const isChecked = Boolean(value?.[option.fieldKey]);
      const isOtherOption = option.isOther ?? false;
      const otherTitleFieldName = option.otherTitleFieldName || "title";
      const descriptionText = getOptionDescription(option);

      return (
        <View
          key={option.fieldKey}
          style={index === config.options.length - 1 ? {} : { marginBottom: optionSpacing }}>
          {/* Other 选项特殊处理 */}
          {isOtherOption ? (
            <View style={{ marginTop: -20 }}>
              <View style={{ flexDirection: "row" }}>
                <CommonCheckbox
                  value={getFieldLabel({ name: "", label: option.label, type: "input" })}
                  checked={isChecked}
                  onChange={() => {
                    handleCheckboxChange(option, !isChecked);
                  }}
                />
                <AsInput
                  placeholder={
                    typeof option.otherPlaceholder === "function"
                      ? option.otherPlaceholder()
                      : option.otherPlaceholder || "Other"
                  }
                  value={
                    (value?.[option.fieldKey] as Record<string, any>)?.[otherTitleFieldName] || ""
                  }
                  hideErrorSpace
                  required
                  onValueChange={val => {
                    const currentOther =
                      (value?.[option.fieldKey] as Record<string, any>) ||
                      option.createInitialValue();
                    const newOther = {
                      ...currentOther,
                      [otherTitleFieldName]: val,
                    };
                    handleSubFieldChange(option.fieldKey, newOther);
                    // 如果有输入内容，自动选中 other 选项
                    if (!isChecked && val.trim()) {
                      handleCheckboxChange(option, true);
                    }
                  }}
                  onFocus={() => {
                    // 点击输入框时，如果未选中则自动选中
                    if (!isChecked) {
                      handleCheckboxChange(option, true);
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </View>
              {/* 描述文本 */}
              {descriptionText ? (
                <Text
                  style={{
                    marginTop: margin.m8,
                    marginLeft: margin.m24,
                    color: colors.white18,
                    fontSize: IS_WEB ? fontSize.f14 : fontSize.f12,
                    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
                  }}>
                  {descriptionText}
                </Text>
              ) : null}
            </View>
          ) : (
            <View>
              <CommonCheckbox
                value={getFieldLabel({ name: "", label: option.label, type: "input" })}
                checked={isChecked}
                label={getFieldLabel({ name: "", label: option.label, type: "input" })}
                onChange={() => {
                  handleCheckboxChange(option, !isChecked);
                }}
              />
              {/* 描述文本 */}
              {descriptionText ? (
                <Text
                  style={{
                    marginTop: margin.m8,
                    marginLeft: margin.m24,
                    color: colors.white18,
                    fontSize: IS_WEB ? fontSize.f14 : fontSize.f12,
                    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
                  }}>
                  {descriptionText}
                </Text>
              ) : null}
            </View>
          )}
          {/* 如果选中且有对应的子表单，在选项下方渲染 */}
          {isChecked ? renderSubForm(option) : null}
        </View>
      );
    },
    [
      value,
      config.options,
      optionSpacing,
      handleCheckboxChange,
      handleSubFieldChange,
      renderSubForm,
      getOptionDescription,
    ],
  );

  return (
    <AsFormItemLayout
      label={label}
      error={error}
      tip={tip}
      hideErrorSpace={hideErrorSpace}
      required={required}
      subLabel={subLabel}
      labelElement={
        <KycLabelElement
          label={label}
          sequenceNumber={sequenceNumber}
          tip={tip}
          required={required}
        />
      }
      style={style}>
      <GenericFormProvider genericFormType="information">
        <View style={{ flex: 1, paddingLeft: isQuestionnaireForm ? padding.p40 : 0 }}>
          {config.options.map((option, index) => renderOption(option, index))}
        </View>
      </GenericFormProvider>
    </AsFormItemLayout>
  );
}

// 使用 memo 优化性能
const MemoizedMultiCheckboxSubForm = memo(MultiCheckboxSubForm, (prevProps, nextProps) => {
  return (
    Boolean(isEqual(prevProps.value, nextProps.value)) &&
    prevProps.label === nextProps.label &&
    prevProps.error === nextProps.error &&
    prevProps.sequenceNumber === nextProps.sequenceNumber &&
    prevProps.tip === nextProps.tip &&
    prevProps.hideErrorSpace === nextProps.hideErrorSpace &&
    prevProps.required === nextProps.required &&
    prevProps.config === nextProps.config
  );
}) as typeof MultiCheckboxSubForm;

export default MemoizedMultiCheckboxSubForm;
